/**
 * One row on a school's printed summary sheet, in Abner's target print
 * (Figma 151:2, "Page 6 · Summary"; his note 137:1722: "Change subtitles on
 * items row to be the packages description"):
 *
 *   ┌──────────────────────────────────────────────────────────────────────────┐
 *   │ 325x   Beef Soft Taco                                  1 Taco per serving │
 *   ├──────────────────────────────────────────────────────────────────────────┤
 *   │ 15x Standard Hotel Pans w/ 20   Hot  Kitchen [  ] °F  Drop-off [  ] °F  Received [ ] │
 *   │ 1x Standard Hotel Pan w/ 13                                               │
 *   │ 1x Standard Hotel Pan w/ 12                                               │
 *   └──────────────────────────────────────────────────────────────────────────┘
 *
 * The top band is the count, the item and its serving; the gray band is the
 * package description (one line per container size, `print-packages.ts`), the
 * temperature, the two transport boxes the packer and the driver fill in, and
 * the Received box school staff tick at drop-off (PROD-1453). The package is
 * said once, in the band. No photos: paper.
 */

import { useMemo } from 'react'

import { PRINT_SECONDARY } from '@/components/chefs/print/print-sheet-heading'
import { type PrintPackageLine, type PrintSummaryRowWords, summaryRowWords } from '@/lib/chefs/print-packages'
import type { Deliverable } from '@/lib/chefs/types'

/** Keeps the strip's gray on paper: Chrome drops backgrounds unless told the colour matters. */
const STRIP_CLASS =
  'rounded-b-md border-t border-[#999] bg-[#f2f2f2] px-2.5 py-1 [-webkit-print-color-adjust:exact] [print-color-adjust:exact]'

/** An empty box to write a temperature in, with its label: "Kitchen [    ] °F". */
function TempField({ label }: { label: string }) {
  return (
    <span className={`flex items-center gap-1.5 ${PRINT_SECONDARY}`}>
      {label}
      <span aria-hidden className="block h-5 w-14 border border-dashed border-[#888] bg-white" />
      °F
    </span>
  )
}

/** The box school staff tick when the row arrived as listed: "Received [ ]" (PROD-1453). */
function ReceivedField() {
  return (
    <span className={`flex items-center gap-1.5 ${PRINT_SECONDARY}`}>
      Received
      <span aria-hidden className="block h-4 w-4 border border-[#888] bg-white" />
    </span>
  )
}

/** "**15x** Standard Hotel Pans w/ **20**": the counts bold, the words regular. */
export function PrintPackageLineText({ line }: { line: PrintPackageLine }) {
  return (
    <span data-testid="print-package-line" className="block">
      {line.count ? <span className="font-semibold tabular-nums">{line.count} </span> : null}
      {line.name}
      {line.fill !== null ? (
        <>
          {' w/ '}
          <span className="font-semibold tabular-nums">{line.fill}</span>
        </>
      ) : null}
    </span>
  )
}

/** The summary row itself, for a deliverable and for a milk row. */
export function PrintSummaryRow({ words }: { words: PrintSummaryRowWords }) {
  const numeral = words.qty.replace(/x$/, '')
  return (
    <div data-testid="print-strip-row" className="break-inside-avoid rounded-md border border-[#999]">
      <div
        data-testid="print-strip-lead"
        className="grid grid-cols-[100px_minmax(0,1fr)_auto] items-baseline gap-x-2 px-2.5 pb-1 pt-1.5">
        <span>
          <span className="font-semibold tabular-nums">{numeral}</span>
          <span className="text-[#444]">x</span>
        </span>
        <span className="font-medium">{words.name}</span>
        {words.perServing ? <span className={PRINT_SECONDARY}>{words.perServing}</span> : <span />}
      </div>
      <div data-testid="print-strip-band" className={STRIP_CLASS}>
        <div className="grid grid-cols-[minmax(0,1fr)_64px_136px_136px_84px] items-center text-[9pt]">
          <span className="min-w-0 text-[#222]">
            {words.packages.map((line, i) => (
              <PrintPackageLineText key={`${line.name}-${i}`} line={line} />
            ))}
            {words.notes.map((note) => (
              <span key={note} data-testid="print-strip-note" className="block text-[8.5pt] text-[#444]">
                {note}
              </span>
            ))}
            {words.tags.length > 0 ? (
              <span className="block">
                {words.tags.map((tag) => (
                  <span key={tag} className="mr-1.5 whitespace-nowrap text-[8pt] font-semibold uppercase tracking-wide">
                    {tag}
                  </span>
                ))}
              </span>
            ) : null}
          </span>
          <span className="font-semibold text-black">{words.temperature}</span>
          <TempField label="Kitchen" />
          <TempField label="Drop-off" />
          <ReceivedField />
        </div>
      </div>
    </div>
  )
}

/** A deliverable as its summary row (`summaryRowWords`). */
export function PrintDeliverableRow({ deliverable }: { deliverable: Deliverable }) {
  const words = useMemo(() => summaryRowWords(deliverable), [deliverable])
  return <PrintSummaryRow words={words} />
}
